import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Typography } from "@mui/material";
import type { FunctionComponent } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import AlbInput from "@/components/common/AlbInput";
import { useSendForgotPasswordCode } from "@/hooks/use-forgot-password";

const AuthenticateEmailSchema = z.object({
  email: z.string().email("invalidEmail"),
});

type AuthenticateEmailSchemaType = z.infer<typeof AuthenticateEmailSchema>;
type Props = {
  value: string;
  onSuccess: (email: string, info?: string) => void;
  identifier: string;
  remark?: string;
  loading?: boolean;
};

export const AuthenticateEmail: FunctionComponent<Props> = ({
  value,
  onSuccess,
  identifier: _identifier,
  remark,
}) => {
  const { t } = useTranslation();

  const methods = useForm({
    resolver: zodResolver(AuthenticateEmailSchema),
    defaultValues: { email: value || "" },
    mode: "onChange",
  });

  const mutation = useSendForgotPasswordCode();

  const loading = mutation.isPending;
  const serverError = (mutation.error as Error | undefined)?.message;

  const handleSubmit = async (data: AuthenticateEmailSchemaType) => {
    await mutation.mutateAsync({ username: data.email });
    // Build a friendly info message if backend returns delivery details
    const info = t("login.code-sent");
    onSuccess(data.email, info);
  };

  return (
    <FormProvider {...methods}>
      <Box
        onSubmit={methods.handleSubmit(handleSubmit)}
        component="form"
        sx={{
          maxWidth: 320,
          width: "100%",
          mx: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <AlbInput
          name="email"
          label="Email"
          type="TextField"
          inputType="email"
          variant="outlined"
        />

        {/* Reserve space for info/error to avoid layout shift */}
        <Box sx={{ minHeight: 32, width: "100%" }}>
          {serverError && (
            <Typography sx={{ fontSize: "0.75rem", color: "error.main", textAlign: "center" }}>
              {serverError}
            </Typography>
          )}
          {!serverError && remark && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              {remark}
            </Typography>
          )}
        </Box>

        <Button
          type="submit"
          disabled={!methods.formState.isValid || Boolean(loading)}
          variant="contained"
          fullWidth
          sx={{
            mt: 1.5,
            bgcolor: "#00244e",
            "&:hover": {
              bgcolor: "#ff5c8d",
            },
            color: "white",
            fontWeight: "bold",
            borderRadius: 2,
            textTransform: "none",
            py: 1,
            fontSize: "0.9rem",
          }}
        >
          {loading ? t("common.form.state.loading") : t("common.navigation.next")}
        </Button>
      </Box>
    </FormProvider>
  );
};
